import React, {useState, useCallback, useEffect} from 'react';
import {
  GoogleMap,
  MarkerF,
  Autocomplete,
  Circle,
  useJsApiLoader,
  Libraries
} from '@react-google-maps/api';
import {
  Box,
  TextField,
  Dialog,
  DialogContent,
  IconButton,
  Alert,
  Snackbar,
  CircularProgress,
  Slider
} from '@mui/material';
import ResponsiveTypography from '@/components/ui/responsive-typography';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import DoneIcon from '@mui/icons-material/Done';
import {useTranslation} from 'react-i18next';
import {
  LocationExtended,
  Location as LocationType,
  defaultLocation
} from '@/schemas/sub-schemas';
import ClearIcon from '@mui/icons-material/Clear';
import {env} from '@/config';

const DEFAULT_LOCATION = {lat: 50.9375, lng: 6.9603}; // Cologne location

const {GOOGLE_MAPS_API_KEY} = env;
const LIBRARIES = ['places'] as Libraries;

export interface LocationProps {
  onAutocompleteLoaded?: (
    autocomplete: google.maps.places.Autocomplete | null
  ) => void;
  open: boolean;
  onClose: () => void;
  onlyCity?: boolean;
  initialLocation: LocationType | null;
  onChange?: (newLocation: LocationType | LocationExtended | null) => void;
  allowClear?: boolean;
  showCircle?: boolean;
  locationExtended?: boolean;
  height?: string;
}

const LocationInputDialog = ({
  onAutocompleteLoaded,
  open,
  onClose,
  onlyCity,
  initialLocation,
  onChange,
  allowClear,
  locationExtended = false,
  showCircle,
  height = '600px'
}: LocationProps) => {
  const {t} = useTranslation();

  const [location, setLocation] = useState<
    LocationType | LocationExtended | null
  >(initialLocation);
  const [markerPosition, setMarkerPosition] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const sureLocation = location || defaultLocation;
  const [address, setAddress] = useState(sureLocation.label);
  const [isValid, setIsValid] = useState(location !== null);
  const [bounds, setBounds] = useState<google.maps.LatLngBounds | null>(null);
  const [autocomplete, setAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isFetchingCurrentPosition, setIsFetchingCurrentPosition] =
    useState(false);
  const [radius, setRadius] = useState<number>(15000);
  const {isLoaded} = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES
  });

  const getBoundsFromLocation = useCallback(
    (location: Pick<LocationType, 'lat' | 'lng'>) => {
      if (isLoaded) {
        const circle = new google.maps.Circle({
          center: {lat: location.lat, lng: location.lng},
          radius: 15000 // Search radius of 15 km
        });
        const bounds = circle.getBounds();
        return bounds;
      }
      return null;
    },
    [isLoaded]
  );

  useEffect(() => {
    if (isLoaded) {
      setBounds(getBoundsFromLocation(location || defaultLocation));
    }
  }, [getBoundsFromLocation, isLoaded, location]);

  const debouncedGeocode = useCallback(
    (address: string) => {
      const geocoder = new google.maps.Geocoder();
      geocoder.geocode(
        {address: address, bounds: bounds},
        (results, status) => {
          if (
            status === google.maps.GeocoderStatus.OK &&
            results &&
            results.length > 0
          ) {
            const place = results[0];
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();

            setMarkerPosition({lat, lng});
            setAddress(place.formatted_address || '');

            updateBounds(lat, lng);

            setLocation(
              locationExtended
                ? {lat, lng, label: place.formatted_address, distance: radius}
                : {lat, lng, label: place.formatted_address}
            );
          }
        }
      );
    },
    [bounds, locationExtended, radius]
  );

  const handleClearClick = useCallback(() => {
    setLocation(null);
    setMarkerPosition(null);
    setAddress('');
    setIsValid(true);
    if (onChange) {
      onChange(null);
    }
  }, [onChange]);

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAddress(e.target.value);
    setIsValid(false);
    // debouncedGeocode(e.target.value);
    if (!isValid && e.target.value !== '') {
      setErrorMessage(`${t('validation:address_invalid')}`);
      setShowSnackbar(true);
    } else {
      setShowSnackbar(false);
    }
  };

  const handleRadiusChange = useCallback(
    (event: Event, newValue: number | number[]) => {
      const newRadius = newValue as number;
      setRadius(newRadius);

      if (markerPosition) {
        setLocation((prevLocation) =>
          prevLocation
            ? {
                ...prevLocation,
                distance: newRadius
              }
            : null
        );
      }
    },
    [markerPosition]
  );

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      const geocoder = new google.maps.Geocoder();
      geocoder.geocode(
        {address: address, bounds: bounds},
        (results, status) => {
          if (
            status === google.maps.GeocoderStatus.OK &&
            results &&
            results.length > 0
          ) {
            const place = results[0];
            const lat = place.geometry.location.lat();
            const lng = place.geometry.location.lng();

            setMarkerPosition({lat, lng});
            setAddress(place.formatted_address || '');

            updateBounds(lat, lng);

            setLocation(
              locationExtended
                ? {lat, lng, label: place.formatted_address, distance: radius}
                : {lat, lng, label: place.formatted_address}
            );
          }
        }
      );
    }
  };

  const handleDoneClick = () => {
    if (allowClear && address === '') {
      if (onChange) {
        onChange(null);
      }
      onClose();
    } else if (address !== '' && isValid) {
      setLocation(
        locationExtended
          ? {
              lat: markerPosition?.lat || DEFAULT_LOCATION.lat,
              lng: markerPosition?.lng || DEFAULT_LOCATION.lng,
              label: address,
              distance: radius
            }
          : {
              lat: markerPosition?.lat || DEFAULT_LOCATION.lat,
              lng: markerPosition?.lng || DEFAULT_LOCATION.lng,
              label: address
            }
      );
      if (onChange) {
        onChange(location);
      }
      onClose();
    }
  };

  const handleMapClick = (e: google.maps.MapMouseEvent) => {
    const lat = e.latLng?.lat();
    const lng = e.latLng?.lng();
    if (lat && lng) {
      setMarkerPosition({lat, lng});
      updateAddress(lat, lng);
      updateBounds(lat, lng);
      setLocation(
        locationExtended
          ? {lat, lng, label: address, distance: radius}
          : {lat, lng, label: address}
      );
    }
  };

  const onLoad = useCallback(
    (autocomplete: google.maps.places.Autocomplete) => {
      setAutocomplete(autocomplete);
      if (onAutocompleteLoaded) {
        onAutocompleteLoaded(autocomplete);
      }
    },
    [onAutocompleteLoaded]
  );

  const updateBounds = (lat: number, lng: number) => {
    const circle = new google.maps.Circle({
      center: {lat, lng},
      radius: 15000 // Search radius of 15 km
    });
    const bounds = circle.getBounds();
    setBounds(bounds);
  };
  const updateAddress = useCallback(
    async (lat: number, lng: number) => {
      const geocoder = new google.maps.Geocoder();
      const latLng = new google.maps.LatLng(lat, lng);

      const bounds = new google.maps.Circle({
        center: latLng
      }).getBounds();
      setBounds(bounds);

      geocoder.geocode(
        {
          location: latLng,
          bounds: bounds
        },
        (results, status) => {
          if (
            status === google.maps.GeocoderStatus.OK &&
            results &&
            results.length > 0
          ) {
            let addressToUse = '';

            if (onlyCity) {
              const cityComponent = results[0].address_components.find(
                (component) => component.types.includes('locality')
              );
              if (cityComponent) {
                addressToUse += cityComponent.long_name;
              }

              const sublocalityComponent = results[0].address_components.find(
                (component) => component.types.includes('sublocality')
              );
              if (sublocalityComponent) {
                if (addressToUse !== '') {
                  addressToUse += ', ';
                }
                addressToUse += sublocalityComponent.long_name;
              }

              if (addressToUse === '') {
                addressToUse = results[0].formatted_address || '';
              }
            } else {
              addressToUse = results[0].formatted_address || '';
            }

            setAddress(addressToUse);
            setLocation(
              locationExtended
                ? {lat, lng, label: addressToUse, distance: radius}
                : {lat, lng, label: addressToUse}
            );

            setIsValid(true);
          } else if (allowClear) {
            setAddress('');
            setIsValid(true);
          } else {
            setAddress('');
            if (!allowClear) {
              setIsValid(false);
            }
          }
        }
      );
    },
    [allowClear, onlyCity, locationExtended, radius]
  );

  const onPlaceChanged = useCallback(() => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();

      if (!place.geometry || !place.geometry.location) {
        //window.alert("No details available for input: '" + place.name + "'");
        return;
      }

      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      setMarkerPosition({lat, lng});

      updateBounds(lat, lng);
      setLocation(
        locationExtended
          ? {lat, lng, label: address, distance: radius}
          : {lat, lng, label: address}
      );

      let formattedAddress = place.formatted_address || '';

      if (onlyCity) {
        const cityComponent = place.address_components?.find((component) =>
          component.types.includes('locality')
        );
        const neighborhoodComponent = place.address_components?.find(
          (component) =>
            component.types.includes('sublocality') ||
            component.types.includes('neighborhood')
        );
        const city = cityComponent ? cityComponent.long_name : '';
        const neighborhood = neighborhoodComponent
          ? neighborhoodComponent.long_name
          : '';

        if (city && neighborhood) {
          formattedAddress = `${neighborhood}, ${city}`;
        } else if (city) {
          formattedAddress = city;
        }
      }

      setAddress(formattedAddress);

      setIsValid(true);

      setShowSnackbar(false);

      debouncedGeocode(formattedAddress);
    }
  }, [
    address,
    autocomplete,
    debouncedGeocode,
    onlyCity,
    locationExtended,
    radius
  ]);

  const getCurrentPosition = useCallback(() => {
    setIsFetchingCurrentPosition(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setMarkerPosition({lat, lng});
        updateAddress(lat, lng);
        updateBounds(lat, lng);
        setLocation(
          locationExtended
            ? {lat, lng, label: address, distance: radius}
            : {lat, lng, label: address}
        );

        setIsFetchingCurrentPosition(false);
      },
      () => {
        window.alert('Unable to fetch location');
        setIsFetchingCurrentPosition(false);
      }
    );
  }, [address, updateAddress, locationExtended, radius]);

  useEffect(() => {
    if (initialLocation) {
      setLocation(initialLocation);
      setMarkerPosition({lat: initialLocation.lat, lng: initialLocation.lng});
      setAddress(initialLocation.label);
    } else {
      setLocation(defaultLocation);
      setMarkerPosition({lat: defaultLocation.lat, lng: defaultLocation.lng});
      setAddress(defaultLocation.label);
    }
  }, [initialLocation]);

  if (!isLoaded) {
    return <div>Loading Google Maps...</div>;
  }
  return (
    <Box>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box sx={{width: '90%'}}>
              <Autocomplete
                onLoad={onLoad}
                onPlaceChanged={onPlaceChanged}
                bounds={bounds ?? undefined}
              >
                <TextField
                  fullWidth
                  label={t('address')}
                  value={address}
                  onChange={handleAddressChange}
                  onKeyPress={handleKeyPress}
                />
              </Autocomplete>
            </Box>

            <Box display="flex" alignItems="center">
              {allowClear && (
                <IconButton onClick={handleClearClick} sx={{marginLeft: 1}}>
                  <ClearIcon color="action" />
                </IconButton>
              )}
              <IconButton onClick={getCurrentPosition} sx={{marginLeft: 1}}>
                <MyLocationIcon color="action" />
              </IconButton>
              <IconButton
                sx={{marginLeft: 1}}
                disabled={!isValid && (!allowClear || address !== '')}
                onClick={handleDoneClick}
              >
                <DoneIcon color="action" />
              </IconButton>
            </Box>
          </Box>
          <Box mt={2}></Box>
          {isLoaded && (
            <>
              <GoogleMap
                mapContainerStyle={{width: '100%', height: height}}
                center={markerPosition || DEFAULT_LOCATION}
                zoom={markerPosition ? (locationExtended ? 11 : 14) : 12}
                options={{fullscreenControl: false, streetViewControl: false}}
                onClick={handleMapClick}
              >
                {markerPosition && <MarkerF position={markerPosition} />}
                {markerPosition && showCircle && radius && (
                  <Circle
                    center={markerPosition}
                    radius={radius}
                    options={{
                      fillColor: '#AA0000',
                      fillOpacity: 0.2,
                      strokeColor: '#AA0000',
                      strokeOpacity: 0.5,
                      clickable: false
                    }}
                  />
                )}
              </GoogleMap>
              {showCircle && (
                <Box mt={2}>
                  <ResponsiveTypography>
                    {t('search_area_km')}
                  </ResponsiveTypography>
                  <Slider
                    value={radius / 1000}
                    onChange={(event, newValue) =>
                      handleRadiusChange(event, (newValue as number) * 1000)
                    }
                    aria-labelledby="radius-slider"
                    min={1}
                    max={50}
                    step={0.5}
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${value} km`}
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSnackbar(false)}
      >
        <Alert
          onClose={() => setShowSnackbar(false)}
          severity="error"
          sx={{width: '100%'}}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      {isFetchingCurrentPosition && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bgcolor="rgba(255, 255, 255, 0.5)"
          zIndex={2000}
        >
          <CircularProgress />
        </Box>
      )}
    </Box>
  );
};

export default LocationInputDialog;
